"use client";

import { Instagram, Youtube } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const TiktokIcon = ({
  className
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.75a4.85 4.85 0 01-1.01-.06z" />
  </svg>;
const AstrobinIcon = ({
  className
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z" />
  </svg>;
const siteLinksCol1 = [{
  label: "OBSERVATIONS",
  to: "https://solobservatory.com/observations/"
}, {
  label: "MISSION LOGS",
  to: "https://solobservatory.com/mission-logs/"
}];
const siteLinksCol2 = [{
  label: "RESEARCH",
  to: "https://solobservatory.com/research/"
}, {
  label: "ABOUT",
  to: "https://solobservatory.com/about/"
}];
const tickerWords = ["SOLAR", "LUNAR", "PLANETARY", "DEEP-SKY", "SCIENCE"];
const socialLinks = [{
  Icon: Instagram,
  label: "Instagram",
  to: "https://www.instagram.com/solobservatory"
}, {
  Icon: TiktokIcon,
  label: "TikTok",
  to: "https://www.tiktok.com/@solobservatory"
}, {
  Icon: Youtube,
  label: "YouTube",
  to: "https://www.youtube.com/@solobservatory"
}, {
  Icon: AstrobinIcon,
  label: "AstroBin",
  to: "https://www.astrobin.com/users/dolphyking/"
}];
const legalLinks = [{
  label: "PRIVACY",
  to: "#"
}, {
  label: "TERMS",
  to: "#"
}, {
  label: "ACCESSIBILITY",
  to: "#"
}];
export default function ObservatoryFooter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="1274" id="observatory-footer" className="relative bg-background text-foreground overflow-hidden" style={{
    clipPath: "polygon(0 4%, 100% 0, 100% 100%, 0 100%)"
  }}>
      <div className="absolute top-0 left-0 w-2 h-2 bg-secondary rounded-lg z-10" style={{
      transform: "translate(0, 2vw)"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative pt-20 pb-10 px-6 md:px-12 lg:px-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="flex items-center gap-3 mb-8 max-w-[1400px] mx-auto">
          <div className="w-6 h-px bg-secondary" />
          <span className="font-mono text-xs tracking-[0.18em] uppercase text-foreground/60">
            Website under construction!
          </span>
        </div>

        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 pb-10">
          <div className="md:col-span-4 flex flex-col gap-4">
            <Link to="/" aria-label="SolObservatory home">
              <WvcLogo className="h-9 w-auto" />
            </Link>

            <p className="font-default text-base md:text-lg text-foreground/80 leading-relaxed max-w-sm">
              A window into the solar and deep-space cosmos.&nbsp;
            </p>

            <span className="font-mono text-xs tracking-[0.15em] uppercase text-foreground/60">
              50° 16' 01" N / 119° 16' 19" W
            </span>
          </div>

          <div className="md:col-span-2 flex flex-col gap-4">
            <h4 className="font-default text-sm uppercase tracking-[0.18em] text-foreground/60">
              NAVIGATE
            </h4>
            <ul className="flex flex-col gap-3">
              {siteLinksCol1.map((item, i) => <li data-index={i} key={item.label}>
                  <Link to={item.to} className="font-default text-base uppercase tracking-wide text-foreground hover:text-secondary hover:underline underline-offset-[6px] transition-colors duration-200">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="md:col-span-2 flex flex-col gap-4">
            <h4 className="font-default text-sm uppercase tracking-[0.18em] text-foreground/60 md:opacity-0 md:select-none" aria-hidden="true">
              &nbsp;
            </h4>
            <ul className="flex flex-col gap-3">
              {siteLinksCol2.map((item, i) => <li data-index={i} key={item.label}>
                  <Link to={item.to} className="font-default text-base uppercase tracking-wide text-foreground hover:text-secondary hover:underline underline-offset-[6px] transition-colors duration-200">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="md:col-span-2 flex flex-col gap-4">
            <h4 className="font-default text-sm uppercase tracking-[0.18em] text-foreground/60">
              CONTACT
            </h4>

            <div className="flex flex-col gap-2 font-sans text-sm text-foreground/90">
              <Link to="mailto:observations@solobservatory.com" className="hover:text-secondary transition-colors break-all">
                solobservatory@gmail.com
              </Link>

            </div>
          </div>

          <div className="md:col-span-2 flex flex-col gap-4">
            <h4 className="font-default text-sm uppercase tracking-[0.18em] text-foreground/60">
              FOLLOW
            </h4>

            <div className="flex gap-2 flex-nowrap">
              {socialLinks.map((item, i) => {
              const Icon = item.Icon;
              return <Link key={item.label} to={item.to} aria-label={item.label} className="w-9 h-9 shrink-0 border border-foreground/20 flex items-center justify-center text-foreground hover:bg-secondary hover:border-secondary hover:text-primary-foreground transition-all duration-200 rounded-md">
                    <Icon className="w-4 h-4" />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        <div className="relative left-1/2 -translate-x-1/2 w-screen overflow-hidden border-y border-foreground/10 py-4">
          <div className="flex gap-12 whitespace-nowrap" style={{
          animation: "ticker-scroll 40s linear infinite",
          width: "max-content"
        }}>
            {[...tickerWords, ...tickerWords].map((word, i) => <span data-index={i} key={i} className="font-default text-2xl md:text-4xl uppercase tracking-wide text-foreground/90 flex items-center gap-12">
                {word}
                <span className="text-secondary">·</span>
              </span>)}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto pt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <span className="font-mono text-xs tracking-[0.15em] uppercase text-foreground/60">
            © 2026 SOLOBSERVATORY
          </span>

          <ul className="flex flex-wrap gap-6">
            {legalLinks.map((item, i) => <li data-index={i} key={item.label}>
                <Link to={item.to} className="font-mono text-xs tracking-[0.15em] uppercase text-foreground/60 hover:text-secondary transition-colors">
                  {item.label}
                </Link>
              </li>)}
          </ul>
        </div>
      </div>

      <style>{`
        @keyframes ticker-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}